import { Circle } from '@mui/icons-material';
import { Box, Paper } from '@mui/material';
import { BoxPlotTooltipProps } from '@nivo/boxplot';
import { ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useSxStyles } from '~/scenes/PortalModules/Analytics/Rewarding/CompensationEqualityReport/CompensationEqualityCharts/CompensationBoxPlotTooltip/useStyles';
import { mergeSxProps } from '~/utils/helper';

const TooltipValueItem = (props: { label: string; value: ReactNode }) => {
  const { label, value } = props;

  const sxStyles = useSxStyles();
  return (
    <>
      <Box sx={sxStyles.label}>{`${label}:`}</Box>
      <Box sx={sxStyles.valueCell}>{value}</Box>
    </>
  );
};

type PropsType = BoxPlotTooltipProps & {
  currency?: string;
};

export const CompensationBoxPlotTooltip = (props: PropsType) => {
  const {
    formatted: { n, extrema, quantiles, values, mean },
    label,
    color,
    currency,
  } = props;
  const [min, max] = extrema;

  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const formatCurrency = useCallback(
    (value: string) => {
      const number = +value;

      return !!currency ? `${number} ${currency}` : number;
    },
    [currency],
  );

  return (
    <Paper sx={sxStyles.root}>
      <Box sx={sxStyles.title}>
        <Circle sx={mergeSxProps(sxStyles.setColor, { color })} />
        <Box sx={sxStyles.valueTable}>
          <TooltipValueItem label={t('laGender')} value={label} />
        </Box>
      </Box>

      <Box sx={sxStyles.count}>
        <Box sx={sxStyles.valueTable}>
          <TooltipValueItem label={t('laEmployees')} value={n} />
        </Box>
      </Box>

      <Box sx={sxStyles.summary}>
        <Box sx={sxStyles.subtitle}>{t('laSummary')}</Box>
        <Box sx={sxStyles.valueTable}>
          <TooltipValueItem label={t('mean')} value={formatCurrency(mean)} />
          <TooltipValueItem label={t('min')} value={formatCurrency(min)} />
          <TooltipValueItem label={t('max')} value={formatCurrency(max)} />
        </Box>
      </Box>

      <Box sx={sxStyles.quantiles}>
        <Box sx={sxStyles.subtitle}>{t('quantiles')}</Box>
        <Box sx={sxStyles.valueTable}>
          {quantiles.map((quantile, index) => {
            return (
              <TooltipValueItem
                key={`quantile-row-${index}`}
                label={`${quantile}%`}
                value={formatCurrency(values[index])}
              />
            );
          })}
        </Box>
      </Box>
    </Paper>
  );
};
